import React, { useEffect, useState } from 'react';
import { DataTableColumn } from '@common/components/datatable/types';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { FormIcon } from '@modules/forms/components/form-icon';
import BadgeCount from '@common/components/badgeCount';
import Icon from '@common/components/icon';
import { EvaluationActions } from '@modules/performance/components/evaluation-actions/evaluation-action';
import moment from 'moment';
import Tooltip from '@common/components/tooltip';
import {
  EPerformanceEvaluationStatus,
  EvaluationFormTypes,
} from '@common/definitions';
import StatusBadge from '@modules/performance/components/status-badge/status-badge';
import BlocksBar from '@modules/performance/components/block-bar/block-bar';
import { resolveScaleColorForValue } from '@modules/performance/utils/utils';
import LinkWrapper from '@common/components/linkWrapper';
import Profile from '@common/components/profile';

export default () => {
  const { t } = useTranslation();

  const history = useHistory();
  const params = useParams<{ networkId: string }>();
  const basePath = params.networkId
    ? `/networks/${params.networkId}`
    : '/admin';
  const [columns, setColumns] = useState<DataTableColumn[]>([]);

  useEffect(() => {
    const baseColumns: DataTableColumn[] = [
      {
        key: 'status',
        label: t('performance:evaluation_status'),
        options: {
          renderLite: (value: string) => (
            <StatusBadge status={value as EPerformanceEvaluationStatus} />
          ),
        },
      },
      {
        key: 'form',
        label: t('performance:evaluation_form'),
        options: {
          render: (row: any) => (
            <div className="tw-items-center tw-gap-3 tw-py-1 tw-w-full FormTitle">
              <FormIcon form={row.form} />
              <a
                className="tw-font-medium tw-text-gray-textDarkest tw-text-base tw-font-inter"
                onClick={() => {
                  return history.push(
                    `${basePath}/performance/evaluations/${row.id}`
                  );
                }}
                role="button"
              >
                {row.form.title}
              </a>
            </div>
          ),
        },
      },
      {
        key: 'reviewee',
        label: t('performance:employee'),
        options: {
          renderLite: (value: any) => (
            <LinkWrapper
              url={`${basePath}/users/${value.id}/about`}
              className="tw-text-inherit hover:tw-text-inherit hover:tw-underline profile-link"
            >
              <Profile enablePopover size={30} user={value} />
            </LinkWrapper>
          ),
        },
      },
      {
        key: 'community',
        label: t('performance:community'),
        options: {
          render: (row: any) => {
            return (
              <div className="tw-flex tw-flex-wrap tw-gap-1">
                {row.reviewee.networks && row.reviewee.networks.length > 0 ? (
                  <div>
                    {row.reviewee.networks[0].name}
                    {row.reviewee.networks.length > 1 && (
                      <BadgeCount
                        title={row.reviewee.networks.map((func: any) => (
                          <div>{func.name}</div>
                        ))}
                        count={row.reviewee.networks.length - 1}
                      />
                    )}
                  </div>
                ) : (
                  <small>
                    <Trans i18nKey="organisation:users_email_empty" />
                  </small>
                )}
              </div>
            );
          },
        },
      },
      {
        key: 'status',
        label: t('performance:evaluation_progress'),
        options: {
          render: (row: any) => {
            const selfSubmitted =
              row?.reviewee?.status === 'submitted' ||
              Boolean(row?.reviewee?.submitted_at);
            const managerSubmitted = Array.isArray(row?.reviewers)
              ? row.reviewers.some(
                  (r: any) => r?.status === 'submitted' || Boolean(r?.submitted_at)
                )
              : false;

            const isManagerType =
              row?.form?.type === EvaluationFormTypes.MANAGER;

            return (
              <div className="tw-flex tw-flex-row tw-items-center tw-gap-[6px]">
                {!isManagerType && (
                  <div
                    className="tw-border tw-border-solid tw-border-gray-border
                  tw-rounded-full tw-py-[2px] tw-pr-[10px] tw-pl-1"
                  >
                    <div className="tw-flex tw-flex-row tw-items-center tw-gap-1">
                      <Icon
                        type={selfSubmitted ? 'circle_check' : 'circle'}
                        size={14}
                        color={selfSubmitted ? 'green-success' : 'grey-dull'}
                      />
                      <div className="tw-font-medium tw-text-gray-text tw-font-sfpro">
                        {t('performance:self')}
                      </div>
                    </div>
                  </div>
                )}
                <div className="tw-border tw-border-solid tw-border-gray-border tw-rounded-full tw-py-[2px] tw-pr-[10px] tw-pl-1">
                  <div className="tw-flex tw-flex-row tw-items-center tw-gap-1">
                    <Icon
                      type={managerSubmitted ? 'circle_check' : 'circle'}
                      size={14}
                      color={managerSubmitted ? 'green-success' : 'grey-dull'}
                    />
                    <div className="tw-font-medium tw-text-gray-text tw-font-sfpro">
                      {t('performance:manager')}
                    </div>
                  </div>
                </div>
              </div>
            );
          },
        },
      },
      {
        key: 'reviewers',
        label: t('performance:evaluation_manager'),
        options: {
          renderLite: (value: any) => value.map((reviewer: any) => (
            <LinkWrapper
              url={`${basePath}/users/${reviewer.id}/about`}
              className="tw-text-inherit hover:tw-text-inherit hover:tw-underline profile-link"
            >
              <Profile enablePopover size={30} user={reviewer} />
            </LinkWrapper>
            )),
        },
      },
      {
        key: 'created_at',
        label: t('performance:started_at'),
        options: {
          render: (row: any) => (
            <Tooltip
              fullTitle
              title={moment(row.created_at).format('MMMM D, YYYY HH:mm:ss')}
              placement="bottom"
            >
              <div>{moment(row.created_at).format('MMM D, YYYY')}</div>
            </Tooltip>
          ),
        },
      },
      {
        key: 'completed_at',
        label: t('performance:completed_at'),
        options: {
          render: (row: any) => (
            <Tooltip
              fullTitle
              disabled={!row.completed_at}
              title={moment(row.completed_at).format('MMMM D, YYYY HH:mm:ss')}
              placement="bottom"
            >
              <div>
                {row.completed_at
                  ? moment(row.completed_at).format('MMM D, YYYY')
                  : '-'}
              </div>
            </Tooltip>
          ),
        },
      },
      {
        key: 'competency_avg_score',
        label: t('performance:score'),
        options: {
          render: (row: any) => {
            let score = null;
            if (
              row.reviewers &&
              row.reviewers.length > 0 &&
              row.reviewers[0].competency_avg_score !== undefined
            ) {
              score = row.reviewers[0].competency_avg_score;
            }

            const color = resolveScaleColorForValue(
              row.rating_scales || [],
              score,
              '#2563EB'
            );
            const isEmpty = score === null || score === undefined;
            return (
              <div style={{ minWidth: '120px', minHeight: '20px' }}>
                <BlocksBar
                  total={row.rating_scales?.length || 5}
                  filled={score}
                  color={color}
                  isEmpty={isEmpty}
                />
              </div>
            );
          },
        },
      },
      {
        key: 'actions',
        label: '',
        className: 'Table__Action',
        options: {
          size: 20,
          render: (row: any) => {
            const { status } = row;
            const statusSubmitted =
              status === EPerformanceEvaluationStatus.SUBMITTED;
            const statusFinished =
              status === EPerformanceEvaluationStatus.FINISHED;
            const statusStarted =
              status === EPerformanceEvaluationStatus.STARTED;

            return (
              <EvaluationActions
                statusSubmitted={statusSubmitted}
                statusFinished={statusFinished}
                statusStarted={statusStarted}
                onWriteEvaluation={() => history.push(`${basePath}/performance/evaluations/${row.id}`)}
                onFinishEvaluation={() => history.push(`${basePath}/performance/evaluations/${row.id}`)}
                onViewResults={() => history.push(`${basePath}/performance/evaluations/${row.id}`)}
              />
            );
          },
        },
      },
    ];

    setColumns(baseColumns);
  }, [t, basePath]);

  return columns;
};
